import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getFeatures } from '../api/entitlementsApi';
import { getModuleSetupProfiles } from '../api/setupProfilesApi';
import { getTenantModuleAccessSummary } from '../api/mafertappModulesApi';
import { colorTokens } from '../app/designTokens';
import { normalizeCollectionResponse } from '../utils/apiResponse';
import {
  formatBillingStatus,
  formatCapabilityCode,
  formatModuleCode,
  formatModuleLabel,
  formatPermissionFeatureLabel,
  formatSetupProfileCode,
  formatSource,
  formatUnknownCode,
} from '../utils/systemLabels';
import { DataTable } from './DataTable';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Tab, Tabs } from './ui/Tabs';

const { semantic } = colorTokens;

const TAB_MODULES = 'modules';
const TAB_CAPABILITIES = 'capabilities';
const TAB_PERMISSIONS = 'permissions';
const TAB_SETUP = 'setup';

function resolveActiveModules(summary) {
  if (!summary) {
    return [];
  }

  const paid = summary.active_paid_modules || summary.paid_modules || [];
  const future = (summary.future_override_modules || []).filter((item) => item.enabled);

  return [
    ...(summary.core_modules || []),
    ...paid,
    ...(summary.support_modules || []),
    ...future,
  ];
}

function capabilityTitle(item) {
  return item.title_sk
    || item.title
    || item.display_name
    || item.label
    || formatCapabilityCode(item.capability_code || item.code)
    || formatUnknownCode(item.capability_code || item.code);
}

function permissionStrings(item) {
  if (Array.isArray(item.permission_strings) && item.permission_strings.length > 0) {
    return item.permission_strings.join(', ');
  }
  const parts = [item.permission_view, item.permission_add, item.permission_edit, item.permission_delete]
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(', ');
  }
  if (item.permission_prefix) {
    return item.permission_prefix;
  }
  return null;
}

export function TenantEntitlementsTab({ tenantId }) {
  const { t } = useTranslation(['features', 'tenants', 'setupProfiles', 'statuses', 'common']);
  const [searchParams] = useSearchParams();
  const legacyMode = searchParams.get('source') === 'legacy' && searchParams.get('include_legacy') === 'true';

  const [tab, setTab] = useState(TAB_MODULES);
  const [moduleSummary, setModuleSummary] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [setupProfiles, setSetupProfiles] = useState([]);
  const [legacyFeatures, setLegacyFeatures] = useState([]);
  const [showAllSetupProfiles, setShowAllSetupProfiles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const requests = [
        getTenantModuleAccessSummary(tenantId),
        getFeatures({ per_page: 200 }),
        getFeatures({ per_page: 200, scope: 'permissions' }),
        getModuleSetupProfiles({ per_page: 100 }),
      ];

      if (legacyMode) {
        requests.push(getFeatures({ per_page: 200, scope: 'legacy', include_legacy: true }));
      }

      const results = await Promise.allSettled(requests);

      if (results[0]?.status === 'fulfilled') setModuleSummary(results[0].value);
      if (results[1]?.status === 'fulfilled') setCapabilities(normalizeCollectionResponse(results[1].value));
      if (results[2]?.status === 'fulfilled') setPermissions(normalizeCollectionResponse(results[2].value));
      if (results[3]?.status === 'fulfilled') setSetupProfiles(normalizeCollectionResponse(results[3].value));
      if (legacyMode && results[4]?.status === 'fulfilled') setLegacyFeatures(normalizeCollectionResponse(results[4].value));
    } catch {
      setMessage(t('features:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [legacyMode, t, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeModules = useMemo(() => resolveActiveModules(moduleSummary), [moduleSummary]);

  const activeModuleCodeSet = useMemo(() => {
    const codes = new Set(
      activeModules.map((item) => String(item.module_code || '').toLowerCase()).filter(Boolean),
    );
    codes.add('global_settings');
    return codes;
  }, [activeModules]);

  const visibleSetupProfiles = useMemo(() => {
    if (showAllSetupProfiles) {
      return setupProfiles;
    }
    return setupProfiles.filter((profile) => {
      const moduleCode = String(profile.module_code || profile.billing_code || '').toLowerCase();
      if (!moduleCode || profile.is_always_seeded || profile.is_core) {
        return true;
      }
      return activeModuleCodeSet.has(moduleCode)
        || (moduleCode === 'quality_control' && activeModuleCodeSet.has('quality'));
    });
  }, [activeModuleCodeSet, setupProfiles, showAllSetupProfiles]);

  const moduleColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => formatModuleLabel(item, item.module_code) || formatModuleCode(item.module_code),
    },
    {
      id: 'module_code',
      label: t('tenants:features.module_code'),
      render: (item) => (
        <Typography
          component="span"
          sx={{
            fontFamily: 'monospace',
            fontSize: 12,
            color: semantic.text.muted,
          }}
        >
          {item.module_code}
        </Typography>
      ),
    },
    {
      id: 'enabled',
      label: t('features:enabled'),
      render: (item) => (
        <StatusBadge
          status={item.enabled !== false ? 'yes' : 'no'}
          category="generic"
          apiLabel={item.enabled !== false ? t('statuses:yes') : t('statuses:no')}
          size="Sm"
          tone={item.enabled !== false ? 'Success' : 'Neutral'}
        />
      ),
    },
    {
      id: 'source',
      label: t('features:source'),
      render: (item) => formatSource(item.source) || formatUnknownCode(item.source),
    },
  ], [t]);

  const capabilityColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => {
        const code = item.capability_code || item.code;
        return (
          <Box>
            <Typography sx={{ fontSize: 13, color: semantic.text.primary }}>{capabilityTitle(item)}</Typography>
            {code && (
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: semantic.text.muted,
                }}
              >
                {code}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'module_code',
      label: t('tenants:features.module_code'),
      render: (item) => (item.module_code
        ? formatModuleCode(item.module_code)
        : t('common:empty_value')),
    },
    {
      id: 'source',
      label: t('features:source'),
      render: (item) => formatSource(item.source_type || item.access_category || item.source)
        || formatUnknownCode(item.source_type || item.access_category || item.source),
    },
  ], [t]);

  const permissionColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => {
        const featureLabel = formatPermissionFeatureLabel(item)
          || item.module_customer_name
          || t('tenants:features.permission_unnamed');
        return (
          <Box>
            <Typography sx={{ fontSize: 13, color: semantic.text.primary }}>{featureLabel}</Typography>
            {item.label_key && (
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: semantic.text.muted,
                }}
              >
                {item.label_key}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'module_code',
      label: t('tenants:features.module_code'),
      render: (item) => formatModuleCode(item.module_code || item.billing_module_code)
        || t('common:empty_value'),
    },
    {
      id: 'permission_string',
      label: t('tenants:features.permission_string'),
      render: (item) => permissionStrings(item) || t('tenants:features.permission_none'),
    },
  ], [t]);

  const setupColumns = useMemo(() => [
    {
      id: 'profile',
      label: t('setupProfiles:profile'),
      render: (profile) => {
        const code = profile.profile_code || profile.code;
        return profile.title_sk
          || profile.title
          || formatSetupProfileCode(code)
          || formatUnknownCode(code);
      },
    },
    {
      id: 'module_code',
      label: t('tenants:features.module_code'),
      render: (profile) => formatModuleCode(profile.module_code || profile.billing_code)
        || t('common:empty_value'),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (profile) => {
        const moduleCode = String(profile.module_code || profile.billing_code || '').toLowerCase();
        const relevant = !moduleCode
          || profile.is_always_seeded
          || profile.is_core
          || activeModuleCodeSet.has(moduleCode)
          || (moduleCode === 'quality_control' && activeModuleCodeSet.has('quality'));
        return (
          <StatusBadge
            status={relevant ? 'relevant' : 'inactive'}
            category="generic"
            apiLabel={relevant
              ? t('tenants:features.setup_relevant')
              : t('tenants:features.setup_inactive_module')}
            size="Sm"
            tone={relevant ? 'Success' : 'Neutral'}
          />
        );
      },
    },
  ], [t, activeModuleCodeSet]);

  const legacyColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => formatCapabilityCode(item.code) || formatUnknownCode(item.code),
    },
    {
      id: 'enabled',
      label: t('features:enabled'),
      render: (item) => (
        <StatusBadge
          status={item.is_active ? 'yes' : 'no'}
          category="generic"
          apiLabel={item.is_active ? t('statuses:yes') : t('statuses:no')}
          size="Sm"
          tone={item.is_active ? 'Success' : 'Neutral'}
        />
      ),
    },
  ], [t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const billingStatus = moduleSummary?.subscription_status || moduleSummary?.billing_status;

  return (
    <Box>
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

      <Tabs
        appearance="Secondary"
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="standard"
      >
        <Tab appearance="Secondary" value={TAB_MODULES} label={t('tenants:features.tabs.active_modules')} />
        <Tab appearance="Secondary" value={TAB_CAPABILITIES} label={t('tenants:features.tabs.capabilities')} />
        <Tab appearance="Secondary" value={TAB_PERMISSIONS} label={t('tenants:features.tabs.mafertapp_permissions')} />
        <Tab appearance="Secondary" value={TAB_SETUP} label={t('tenants:features.tabs.setup_profiles')} />
      </Tabs>

      {tab === TAB_MODULES && (
        <SectionCard
          variant="Default"
          title={t('tenants:features.tabs.active_modules')}
          subtitle={billingStatus
            ? t('tenants:features.subscription_status', {
              status: formatBillingStatus(billingStatus) || formatUnknownCode(billingStatus),
            })
            : t('tenants:features.no_subscription')}
          noPadding
          sx={{ mb: 0 }}
        >
          <DataTable
            type="Compact"
            embedded
            columns={moduleColumns}
            rows={activeModules}
            getRowKey={(item) => item.module_code}
            emptyMessage={t('tenants:features.no_modules')}
          />
        </SectionCard>
      )}

      {tab === TAB_CAPABILITIES && (
        <SectionCard
          variant="Default"
          title={t('tenants:features.tabs.capabilities')}
          noPadding
          sx={{ mb: 0 }}
        >
          <DataTable
            type="Compact"
            embedded
            columns={capabilityColumns}
            rows={capabilities}
            getRowKey={(item) => item.id || item.capability_code || item.code}
            emptyMessage={t('common:empty_value')}
          />
        </SectionCard>
      )}

      {tab === TAB_PERMISSIONS && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>{t('tenants:features.permissions_preview_hint')}</Alert>
          <SectionCard
            variant="Default"
            title={t('tenants:features.tabs.mafertapp_permissions')}
            noPadding
            sx={{ mb: 0 }}
          >
            <DataTable
              type="Compact"
              embedded
              columns={permissionColumns}
              rows={permissions}
              getRowKey={(item) => item.id || item.label_key || item.permission_view}
              emptyMessage={t('common:empty_value')}
            />
          </SectionCard>
        </>
      )}

      {tab === TAB_SETUP && (
        <>
          <FormControlLabel
            sx={{ mb: 2 }}
            control={(
              <Switch
                checked={showAllSetupProfiles}
                onChange={(event) => setShowAllSetupProfiles(event.target.checked)}
              />
            )}
            label={t('tenants:features.show_all_setup_profiles')}
          />
          <SectionCard
            variant="Default"
            title={t('tenants:features.tabs.setup_profiles')}
            noPadding
            sx={{ mb: 0 }}
          >
            <DataTable
              type="Compact"
              embedded
              columns={setupColumns}
              rows={visibleSetupProfiles}
              getRowKey={(profile) => profile.id}
              emptyMessage={t('setupProfiles:no_profiles')}
            />
          </SectionCard>
        </>
      )}

      {!legacyMode && (
        <Alert severity="info" sx={{ mt: 2 }}>{t('tenants:features.legacy_hidden')}</Alert>
      )}

      {legacyMode && legacyFeatures.length > 0 && (
        <SectionCard
          variant="Default"
          title={t('tenants:features.legacy_title')}
          noPadding
          sx={{ mt: 2, mb: 0 }}
        >
          <DataTable
            type="Compact"
            embedded
            columns={legacyColumns}
            rows={legacyFeatures}
            getRowKey={(item) => item.id}
            emptyMessage={t('common:empty_value')}
          />
        </SectionCard>
      )}
    </Box>
  );
}
